import { Minus, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cartActions } from '@/features/billing/cartStore';
import type { CartLine } from '@/features/billing/pricing';
import { cn, money } from '@/lib/utils';

export function CartLineRow({ line }: { line: CartLine }) {
  const lineTotal = line.qty * line.price;
  const atMaxStock = line.qty >= line.maxStock;

  return (
    <div className="flex items-center gap-2 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{line.shortName || line.name}</p>
        <p className="tabular text-xs text-muted-foreground">
          {money(line.price)} × {line.unit}
          {line.daysToExpiry <= 90 && (
            <span className={cn(line.daysToExpiry < 0 ? 'text-status-expired' : 'text-status-expiring', 'ml-1.5')}>
              · {line.daysToExpiry < 0 ? 'expired' : `${line.daysToExpiry}d left`}
            </span>
          )}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={() => cartActions.changeQty(line.medicineId, -1)}
          aria-label="Decrease quantity"
        >
          <Minus className="h-3 w-3" />
        </Button>
        <Input
          type="text"
          inputMode="numeric"
          value={line.qty}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            if (digits) cartActions.setQty(line.medicineId, Number(digits));
          }}
          className="h-7 w-12 px-1 text-center tabular"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-7 w-7"
          disabled={atMaxStock}
          onClick={() => cartActions.changeQty(line.medicineId, 1)}
          aria-label="Increase quantity"
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>

      <p className="tabular w-20 shrink-0 text-right text-sm font-semibold">{money(lineTotal)}</p>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-status-expired"
        onClick={() => cartActions.removeItem(line.medicineId)}
        aria-label={`Remove ${line.name}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
